'use client';

import React from 'react';
import MuiBox from '@new-components/atoms/MuiBox';
import { MultiSelectSearchBox } from '@new-components/molecules/MultiSelectSearchBox';
import BedroomPopupFilter from '@new-components/organisms/BedroomPopupFilter';
import MuiButton from '@new-components/atoms/MuiButton';
import MuiTypography from '@new-components/atoms/MuiTypography';
import SearchPriceFilter from '@new-components/organisms/SearchPriceFilter';
import MuiDivider from '@new-components/atoms/MuiDivider';
import { Desktop } from '@components/Desktop';
import { useIsDesktop } from '@utils/useisdesktop';
import { Mobile } from '@components/Mobile';
import AvailabilityOption from '@new-components/organisms/AvailabilityOption';
import { SearchIcon, AllFiltersIcon } from './icons';
import {
  useSearchState,
  SearchReducerProps,
  INITIAL_STATE,
  SearchState,
} from './useSearchState';
import { useAdvancedFilter } from '../AdvancedFilters/Provider';
import { IconMap } from './useSearchState';
import { MediaQuery } from '@constants/theme';
import { useSearchOptions } from './useSearchOptions';
import { useRouter } from 'next/navigation';
import { navigate } from '@utils/navigate';
import { deepCompareObjects } from '@utils/deepCompareObjects';
import MuiSkeleton from '@new-components/atoms/MuiSkeleton';
import { getAppRoute } from '@utils/new/navigation';
import { ParsedDataGFR } from '@services/new/common/getFilters';
import { useAppRouter } from '@hooks/useAppRouter';
import { APP_ROUTES } from '@constants/appRoutes';

function DividerContainer({
  isDesktop,
  children,
}: {
  isDesktop: boolean;
  children: React.ReactNode[];
}) {
  if (!isDesktop) return children;
  return children.map((element, index) => {
    if (index < children.length - 1)
      return (
        <>
          {element}
          <MuiDivider
            orientation='vertical'
            flexItem
            variant='middle'
            sx={{
              background: '#82807e1a',
            }}
          />
        </>
      );
    return element;
  });
}
export const getQueryStr = (state: SearchState) => {
  const queryParams = new URLSearchParams(
    Object.fromEntries(
      Object.entries({
        dst: state.searchText
          .filter((item) => item.iconType === 'location')
          .map((item) => item.value)
          .join('_'),
        text: state.searchText
          .filter((item) => item.iconType === 'community')
          .map((item) => item.value)
          .join('_'),
        bdr: state.bedrooms.join(','),
        mpr: state.price.max?.toString() || '',
        spr: state.price.min?.toString() || '',
        ty: state.propertyType.join(',') || '',
        cst: state.availability || '',
        rst: state.saleType || '',
        bhr: state.bathrooms.join(',') || '',
        minar: state.area.min?.toString() || '',
        maxar: state.area.max?.toString() || '',
        uv: state.propertyView || '',
      }).filter(([_, value]) => value !== '' && value.toLowerCase() !== 'any') // Remove empty values
    )
  );
  return queryParams.toString();
};

interface SearchBoxProps {
  suggestions: any;
  filters: ParsedDataGFR;
}
/**
 * SearchBox: Base searchBox which will be extended for other search boxes
 *
 * @param {SearchBoxProps} props Component props
 * @returns {ReactElement} SearchBox component
 */
export const SearchBox: React.FC<SearchBoxProps> = (props) => {
  const [state, dispatch] = useSearchState();
  const advancedFilter = useAdvancedFilter();
  const router = useRouter();
  const { getPathWithBase } = useAppRouter();

  const dispatchAction =
    (key: SearchReducerProps['type']) =>
      (value: SearchReducerProps['payload']) =>
        dispatch({ type: key, payload: value });

  const isDesktop = useIsDesktop();

  const openAdvancedFilter = async () => {
    const data = await advancedFilter.show(state);

    if (data) {
      const queryStr = getQueryStr(data);
      navigate(router, getPathWithBase(APP_ROUTES.UNITS, queryStr))
    }
    // dispatch({type: 'RESET_STATE', payload: data});
  };

  const handleSearch = (e: React.MouseEvent) => {
    const queryStr = getQueryStr(state);
    navigate(router, getPathWithBase(APP_ROUTES.UNITS, queryStr))
  };
  const getFilterStatus = (
    state: SearchState
  ): { isAnyFilterActive: boolean; activeFiltersCount: number } => {
    let activeFiltersCount = 0;
    Object.entries(state).forEach(([key, value]) => {
      if (
        key !== 'searchText' &&
        !deepCompareObjects(value, INITIAL_STATE[key as keyof SearchState])
      ) {
        activeFiltersCount++;
      }
    });
    const isAnyFilterActive = activeFiltersCount > 0;
    return { isAnyFilterActive, activeFiltersCount };
  };
  const { isAnyFilterActive, activeFiltersCount } = getFilterStatus(state);

  return (
    <>
      <MuiBox
        sx={{
          borderRadius: '100px',
          display: 'flex',
          background: '#FFFFFF',
          alignItems: 'center',
          paddingInlineEnd: '12px',
          position: 'relative',
          color: '#2D2D2D',
          [MediaQuery.mobile]: {
            overflow: 'hidden',
          },
          [MediaQuery.desktop]: {
            transform: "scale(1.2)",
            transformOrigin: "top left",
            //adjust filter width
            ['& > button:not(:nth-last-of-type(-n+2))']: {
              minWidth: '124px',
            },
          },
        }}>
        <DividerContainer isDesktop={isDesktop}>
          <MultiSelectSearchBox
            placeholder='Destination or Community'
            options={props.suggestions}
            value={state.searchText}
            onChange={dispatchAction('SET_SEARCH_TEXT')}
            selectionLimit={5}
            iconMap={IconMap}
            showSearchIcon={false}
          />
          <Desktop>
            <BedroomPopupFilter
              title='Beds'
              subTitle='Can choose multiple'
              options={props.filters.bedrooms}
              value={state.bedrooms}
              handleChange={dispatchAction('SET_BEDROOMS')}
            />
          </Desktop>
          <Desktop>
            <SearchPriceFilter
              value={state.price}
              handleChange={dispatchAction('SET_PRICE')}
              min={props.filters.price.min}
              max={props.filters.price.max}
              step={1}
            />
          </Desktop>
          <Desktop>
            <AvailabilityOption
              options={props.filters.completionStatus}
              value={state.availability}
              handleChange={dispatchAction('SET_AVAILABILITY')}
            />
          </Desktop>
          <MuiButton
            id='all-filter-button'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              height: '60px',
              padding: '0px 12px',
              borderRadius: '0px',
              background: '#FFFFFF',
              fontSize: '17px',
              fontWeight: 500,
              lineHeight: '23.8px',
              letterSpacing: '-0.4px',
              textAlign: 'left',
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none',
              color: '#2D2D2D',
              minWidth: '0px',
              [':hover']: { background: '#FFFFFF' },
              [MediaQuery.desktop]:  {
                minWidth: '155px',
              }
            }}
            onClick={openAdvancedFilter}>
            <AllFiltersIcon />
            <Desktop>
              <MuiTypography id="all-filters">All Filters</MuiTypography>
            </Desktop>
            {isAnyFilterActive && <MuiTypography
              sx={{
                minWidth: '24px',
                maxWidth: '24px',
              }}>
              {`(${activeFiltersCount})`}
            </MuiTypography>}
          </MuiButton>
        </DividerContainer>
        <MuiButton
          id='search-button'
          title='search'
          sx={{
            boxShadow: '0px 8px 32px 0px #00000033',
            minWidth: '44px',
            height: '44px',
            width: '44px',
            borderRadius: '100px',
            boxSizing: 'border-box',
            background: '#EB6924',
            marginLeft: '12px',
            [':hover']: { background: '#EB6924' },
          }}
          onClick={handleSearch}>
          <SearchIcon />
        </MuiButton>
        <Mobile>
          <MuiBox
            id='full-page-search-trigger'
            onClick={openAdvancedFilter}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '100%',
              background: 'transparent',
              cursor: 'pointer',
            }}
          />
        </Mobile>
      </MuiBox>
    </>
  );
};
